<script setup>
// NOTICE: KAPTION-INTEGRATION
// contact-first-conversation.DRAFT.1..5 — the EMPTY conversation for a contact
// with no conversation in the chosen inbox. Nothing exists in the database
// until the first send: the window is necessarily closed (the contact never
// wrote to this number), so the only possible first message is a template.
// The picker opens by itself; Enviar creates the conversation with the
// template as its first message (the wizard's sendNow), Programar and pending
// templates create it with a scheduled message (the wizard's scheduleLater).
// Then the agent lands in the real conversation.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { emitter } from 'shared/helpers/mitt';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import KaptionSchedulePopover from 'dashboard/components/widgets/conversation/KaptionSchedulePopover.vue';
import KaptionReplyTemplateButton from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/KaptionReplyTemplateButton.vue';
import KaptionReplyTemplateModal from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/KaptionReplyTemplateModal.vue';
import KaptionPickedTemplateEditor from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/KaptionPickedTemplateEditor.vue';
import {
  buildPickedTemplateSchedulePayload,
  isTemplateCapableInbox,
  isTemplatePending,
  templateCategoryBadge,
} from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/kaptionReplyTemplateHelpers';
import { fetchContactableInboxes } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';
import {
  buildTemplateParams,
  buildWhatsAppConversationPayload,
  renderTemplateBody,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
import {
  CONTACT_FIRST_FLAG,
  phoneToSourceId,
  pickExistingConversation,
} from './kaptionContactFirstHelpers';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  inboxId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { canUseFeature } = useFeaturePermissions();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const getContact = useMapGetter('contacts/getContact');
const getInbox = useMapGetter('inboxes/getInbox');

const isReady = ref(false);
const showPicker = ref(false);
const showSchedule = ref(false);
const isSending = ref(false);
const pickedTemplate = ref(null);
const pickedState = ref({
  bodyValues: {},
  headerMedia: {},
  buttonValues: {},
  isValid: false,
});

const contact = computed(() => getContact.value(Number(props.contactId)) || {});
const inbox = computed(() => getInbox.value(Number(props.inboxId)) || {});
const canUseTemplates = computed(() => canUseFeature('whatsapp_templates'));
// contact-first-conversation.DRAFT.5 — only a WhatsApp (Meta) inbox the agent
// can see; anything else shows an explanation instead of a composer.
const isUsable = computed(
  () =>
    isKaptionFlagEnabled(CONTACT_FIRST_FLAG) &&
    isTemplateCapableInbox(inbox.value) &&
    Boolean(contact.value.id)
);
const isPending = computed(() => isTemplatePending(pickedTemplate.value));
const canSend = computed(
  () => !!pickedTemplate.value && pickedState.value.isValid && !isSending.value
);
const contactName = computed(
  () => contact.value.name || contact.value.phone_number || ''
);

const goToConversation = conversation =>
  router.replace({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversation.id },
  });

const findExistingConversation = async () => {
  try {
    const { data } = await ContactAPI.getConversations(props.contactId);
    return pickExistingConversation(data?.payload || [], props.inboxId);
  } catch {
    return null;
  }
};

const resolveSourceId = async () => {
  try {
    const inboxes = await fetchContactableInboxes(props.contactId);
    const match = inboxes.find(item => item.id === Number(props.inboxId));
    if (match?.sourceId) return match.sourceId;
  } catch {
    // fall back to the phone number
  }
  return phoneToSourceId(contact.value.phone_number);
};

const onPicked = template => {
  pickedTemplate.value = template;
  pickedState.value = {
    bodyValues: {},
    headerMedia: {},
    buttonValues: {},
    isValid: false,
  };
  showPicker.value = false;
};
const clearPicked = () => {
  pickedTemplate.value = null;
  pickedState.value = {
    bodyValues: {},
    headerMedia: {},
    buttonValues: {},
    isValid: false,
  };
  showSchedule.value = false;
};

// The conversation shell for a scheduled or approval-gated first message:
// ConversationBuilder creates it without a message (the wizard's scheduleLater).
const createShell = async sourceId => {
  const { data } = await ConversationApi.create({
    inbox_id: Number(props.inboxId),
    contact_id: Number(props.contactId),
    source_id: sourceId,
    ...(canUseFeature('direct_assignment')
      ? { assignee_id: currentUser.value?.id }
      : {}),
  });
  return data;
};

const scheduleOn = async (conversation, schedule) => {
  await KaptionScheduledMessagesAPI.createScheduledMessage(
    accountId.value,
    conversation.id,
    buildPickedTemplateSchedulePayload({
      template: pickedTemplate.value,
      ...pickedState.value,
      scheduledAt: schedule.scheduledAt,
      timezone: schedule.timezone,
    })
  );
  emitter.emit('kaption-message-scheduled', {
    conversationId: conversation.id,
  });
};

// contact-first-conversation.DRAFT.3 / DRAFT.4
const send = async schedule => {
  if (!canSend.value) return;
  isSending.value = true;
  showSchedule.value = false;
  try {
    // Someone may have opened one meanwhile (or the customer wrote): use it.
    let conversation = await findExistingConversation();
    const effectiveSchedule =
      schedule ||
      (isPending.value
        ? {
            scheduledAt: new Date().toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          }
        : null);

    if (effectiveSchedule) {
      if (!conversation)
        conversation = await createShell(await resolveSourceId());
      await scheduleOn(conversation, effectiveSchedule);
    } else {
      const content = renderTemplateBody(
        pickedTemplate.value,
        pickedState.value.bodyValues
      );
      const templateParams = buildTemplateParams(
        pickedTemplate.value,
        pickedState.value.bodyValues,
        pickedState.value.headerMedia,
        pickedState.value.buttonValues
      );
      if (conversation) {
        await store.dispatch('createPendingMessageAndSend', {
          conversationId: conversation.id,
          message: content,
          private: false,
          templateParams,
        });
      } else {
        conversation = await store.dispatch('contactConversations/create', {
          params: buildWhatsAppConversationPayload({
            inbox: inbox.value,
            contact: contact.value,
            sourceId: await resolveSourceId(),
            content,
            templateParams,
            currentUser: currentUser.value,
          }),
          isFromWhatsApp: true,
        });
      }
    }

    let doneMessage = t('KAPTION_CONTACT_FIRST.DRAFT.SENT');
    if (schedule) doneMessage = t('KAPTION_CONTACT_FIRST.DRAFT.SCHEDULED');
    else if (isPending.value)
      doneMessage = t('KAPTION_CONTACT_FIRST.DRAFT.QUEUED');
    useAlert(doneMessage);
    goToConversation(conversation);
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        t('KAPTION_CONTACT_FIRST.DRAFT.ERROR')
    );
  } finally {
    isSending.value = false;
  }
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('contacts/show', { id: Number(props.contactId) }),
      getInbox.value(Number(props.inboxId))?.id
        ? Promise.resolve()
        : store.dispatch('inboxes/get'),
    ]);
  } catch {
    // handled by the unusable state below
  }
  // contact-first-conversation.RESOLVE.1 — a bookmarked or stale draft URL
  // never duplicates: an existing conversation wins.
  const existing = await findExistingConversation();
  if (existing) {
    goToConversation(existing);
    return;
  }
  isReady.value = true;
  // contact-first-conversation.DRAFT.2 — the window is closed: open the picker.
  if (isUsable.value && canUseTemplates.value) showPicker.value = true;
});
</script>

<template>
  <div
    class="wp-card wp-clip flex flex-col flex-1 min-w-0 w-full bg-n-background border-l border-n-weak"
    data-testid="contact-first-draft"
  >
    <header
      class="flex items-center gap-3 px-4 py-3 border-b shrink-0 border-n-weak"
    >
      <Avatar
        :name="contactName || '?'"
        :src="contact.thumbnail"
        :size="32"
        rounded-full
      />
      <div class="flex flex-col min-w-0">
        <span class="text-sm font-semibold truncate text-n-slate-12">
          {{ contactName }}
          <span
            v-if="contact.name && contact.phone_number"
            class="ml-2 font-normal text-n-slate-10"
          >
            {{ contact.phone_number }}
          </span>
        </span>
        <span class="flex items-center gap-1 text-xs truncate text-n-slate-10">
          <span class="i-ri-whatsapp-line size-3.5" />
          {{ inbox.name }}
        </span>
      </div>
      <span
        class="px-2 py-0.5 ml-auto text-[11px] font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
      >
        {{ t('KAPTION_CONTACT_FIRST.DRAFT.BADGE') }}
      </span>
    </header>

    <div
      class="flex flex-col items-center justify-center flex-1 gap-2 px-8 text-center"
    >
      <span class="i-lucide-message-square-dashed size-8 text-n-slate-9" />
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('KAPTION_CONTACT_FIRST.DRAFT.EMPTY_TITLE') }}
      </p>
      <p class="max-w-md text-xs text-n-slate-10">
        {{
          isReady && !isUsable
            ? t('KAPTION_CONTACT_FIRST.DRAFT.UNAVAILABLE')
            : t('KAPTION_CONTACT_FIRST.DRAFT.EMPTY_HINT')
        }}
      </p>
    </div>

    <div
      v-if="isReady && isUsable"
      class="relative mx-2 mb-2 border rounded-3xl border-n-weak bg-n-solid-1"
      data-testid="contact-first-composer"
    >
      <div class="flex items-center justify-between h-[3.25rem] px-4">
        <span class="text-sm font-medium text-n-slate-11">
          {{ t('KAPTION_CONTACT_FIRST.DRAFT.FIRST_MESSAGE') }}
        </span>
        <KaptionReplyTemplateButton
          v-if="canUseTemplates"
          class="!mt-0"
          :active="!!pickedTemplate"
          @click="showPicker = true"
        />
      </div>

      <KaptionPickedTemplateEditor
        v-if="pickedTemplate"
        :key="`${pickedTemplate.name}-${pickedTemplate.language}`"
        :template="pickedTemplate"
        :contact="contact"
        :disabled="isSending"
        @change="pickedState = $event"
      />
      <button
        v-else
        type="button"
        class="flex items-center w-full gap-2 px-4 py-6 text-sm text-left text-n-slate-10 hover:text-n-slate-12"
        data-testid="contact-first-choose-template"
        :disabled="!canUseTemplates"
        @click="showPicker = true"
      >
        <span class="i-lucide-file-text size-4" />
        {{
          canUseTemplates
            ? t('KAPTION_CONTACT_FIRST.DRAFT.CHOOSE_TEMPLATE')
            : t('KAPTION_CONTACT_FIRST.DRAFT.NO_TEMPLATE_PERMISSION')
        }}
      </button>

      <div class="flex items-center justify-between gap-2 p-3">
        <div
          v-if="pickedTemplate"
          class="flex items-center min-w-0 gap-1.5 h-7 pl-2.5 pr-1 rounded-lg bg-n-teal-3 text-n-teal-11"
          data-testid="picked-template-chip"
        >
          <span class="i-lucide-file-text size-3.5 shrink-0" />
          <span class="text-xs font-medium truncate max-w-[14rem]">
            {{ pickedTemplate.name }}
          </span>
          <span
            class="px-1 py-px text-[10px] font-semibold rounded bg-n-alpha-2 text-n-slate-11 shrink-0"
          >
            {{ templateCategoryBadge(pickedTemplate) }}
          </span>
          <span
            v-if="isPending"
            class="px-1.5 py-px text-[10px] font-semibold rounded bg-n-amber-3 text-n-amber-11 shrink-0"
          >
            {{ t('KAPTION_REPLY_TEMPLATE.CHIP.PENDING') }}
          </span>
          <button
            type="button"
            data-testid="contact-first-cancel-template"
            class="flex items-center gap-1 px-1.5 py-1 text-xs font-medium rounded-md shrink-0 hover:bg-n-teal-4"
            :aria-label="t('KAPTION_REPLY_TEMPLATE.CHIP.CANCEL')"
            @click="clearPicked"
          >
            <span class="i-lucide-x size-3.5" />
            {{ t('KAPTION_REPLY_TEMPLATE.CHIP.CANCEL') }}
          </button>
        </div>
        <span v-else />

        <div class="relative flex items-center gap-2">
          <button
            v-if="canUseFeature('scheduled_messages')"
            v-tooltip.top="t('KAPTION_CONTACT_FIRST.DRAFT.SCHEDULE')"
            type="button"
            data-testid="contact-first-schedule"
            class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
            :aria-label="t('KAPTION_CONTACT_FIRST.DRAFT.SCHEDULE')"
            :disabled="!canSend"
            @click="showSchedule = !showSchedule"
          >
            <span class="i-lucide-clock size-4" />
          </button>
          <KaptionSchedulePopover
            :show="showSchedule"
            @close="showSchedule = false"
            @schedule="send"
          />
          <button
            type="button"
            data-testid="contact-first-send"
            class="flex items-center gap-1.5 h-8 px-3 text-sm font-medium text-white rounded-lg bg-n-brand hover:brightness-110 disabled:opacity-50"
            :disabled="!canSend"
            @click="send(null)"
          >
            {{
              isPending
                ? t('KAPTION_REPLY_TEMPLATE.SEND_WHEN_APPROVED')
                : t('KAPTION_CONTACT_FIRST.DRAFT.SEND')
            }}
            <span class="i-lucide-send-horizontal size-4" />
          </button>
        </div>
      </div>
    </div>

    <KaptionReplyTemplateModal
      v-if="showPicker && isUsable"
      :inbox="inbox"
      :contact-name="contactName"
      :selected-template="pickedTemplate"
      @select="onPicked"
      @close="showPicker = false"
    />
  </div>
</template>
